/**
 * Comment vs draw mode and draw tools for document viewers.
 *
 * The same toolbar serves PDFs, images, and CSVs:
 *   - PDF: Comments / Draw / Measure / Engineer (role-gated)
 *   - CSV (`csvVariant`): Comments / Draw / Highlight (with cell / row / column sub-tools)
 *   - Image: Comments / Draw / Measure / Engineer (same overlay + Firestore model as PDF; page = 1)
 */

import React, { useEffect, useState } from 'react';
import {
  Pencil,
  Highlighter,
  Eraser,
  MessageCircle,
  Ruler,
  Square,
  Rows,
  Columns,
  PenTool,
  MessageSquare,
  Minus,
  Combine,
  Cloud,
  Type,
} from 'lucide-react';
import type { DocumentMarkupTool } from '../../types';
import { ENG_STROKE } from './engineeringMarkup';
import { FASTENER_CATALOG, fastenersByGroup } from './fastenerLibrary';
import { FastenerSymbol } from './FastenerSymbol';
import {
  markupWidthChipPx,
  markupWidthLabel,
  markupWidthsForTool,
  showsMarkupStrokeWidth,
} from './markupStrokeWidth';

export type ViewerInteractionMode = 'comment' | 'draw' | 'measure' | 'highlight' | 'takeoff' | 'engineer';
export type CsvHighlightSubTool = 'cell' | 'row' | 'column';

export interface ViewerModeBarProps {
  mode: ViewerInteractionMode;
  onModeChange: (mode: ViewerInteractionMode) => void;
  drawTool: DocumentMarkupTool;
  onDrawToolChange: (tool: DocumentMarkupTool) => void;
  drawColor: string;
  onDrawColorChange: (color: string) => void;
  drawStrokeWidth?: number;
  onDrawStrokeWidthChange?: (width: number) => void;
  /** Hide the Measure mode (used by CSV viewer where measurement is not wired up). */
  hideMeasure?: boolean;
  /** Add data-tour anchors for the portal PDF markup walkthrough. */
  tourAnchors?: boolean;
  /** When true, surface the Highlight mode for the CSV viewer. */
  csvVariant?: boolean;
  /** Active CSV highlight sub-tool (cell / row / column). */
  highlightTool?: CsvHighlightSubTool;
  onHighlightToolChange?: (tool: CsvHighlightSubTool) => void;
  highlightColor?: string;
  onHighlightColorChange?: (color: string) => void;
  /** Engineers and admins — callouts, pattern lines, fastener stamps. */
  showEngineering?: boolean;
  /** Revision cloud + typewriter. Hidden on the CSV viewer. */
  showShapeTools?: boolean;
  selectedStampId?: string;
  onStampChange?: (stampId: string) => void;
}

const SWATCHES = ['#3b82f6', '#ef4444', '#22c55e', '#eab308', '#a855f7', '#ffffff'];
const ENGINEER_SWATCHES = [ENG_STROKE, '#111111', '#1d4ed8', '#0f766e', '#ffffff'];
const HIGHLIGHT_SWATCHES = ['#fde68a', '#bbf7d0', '#bae6fd', '#fecaca', '#e9d5ff', '#fed7aa'];
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

function tip(label: string, shortcut: string, detail?: string): string {
  return detail ? `${label} · ${shortcut}. ${detail}` : `${label} · ${shortcut}`;
}

const DELETE_UNDO = 'Click a mark, then Delete or × to remove it. ⌘Z / ⌘⇧Z undo and redo.';

export function ViewerModeBar({
  mode,
  onModeChange,
  drawTool,
  onDrawToolChange,
  drawColor,
  onDrawColorChange,
  drawStrokeWidth,
  onDrawStrokeWidthChange,
  hideMeasure,
  tourAnchors,
  csvVariant,
  highlightTool = 'cell',
  onHighlightToolChange,
  highlightColor = HIGHLIGHT_SWATCHES[0],
  onHighlightColorChange,
  showEngineering,
  showShapeTools = true,
  selectedStampId = 'peg',
  onStampChange,
}: ViewerModeBarProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const selectedStamp = FASTENER_CATALOG.find(f => f.id === selectedStampId) || FASTENER_CATALOG[0];
  const strokeWidths = markupWidthsForTool(drawTool);
  const showStrokeWidths = !!onDrawStrokeWidthChange && showsMarkupStrokeWidth(drawTool);

  useEffect(() => {
    if (drawTool === 'zigzag') onDrawToolChange('line');
  }, [drawTool, onDrawToolChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      if (key === '1') {
        e.preventDefault();
        onModeChange('comment');
        return;
      }
      if (key === '2') {
        e.preventDefault();
        onModeChange('draw');
        return;
      }
      if (key === '3' && !hideMeasure && !csvVariant) {
        e.preventDefault();
        onModeChange('measure');
        return;
      }
      if (key === '4' && showEngineering && !csvVariant) {
        e.preventDefault();
        onModeChange('engineer');
        return;
      }

      if (mode === 'draw') {
        if (key === 'p') {
          e.preventDefault();
          onDrawToolChange('pen');
        } else if (key === 'h') {
          e.preventDefault();
          onDrawToolChange('highlighter');
        } else if (key === 'e') {
          e.preventDefault();
          onDrawToolChange('eraser');
        } else if (key === 'c' && showShapeTools && !csvVariant) {
          e.preventDefault();
          onDrawToolChange('cloud');
        } else if (key === 't' && showShapeTools && !csvVariant) {
          e.preventDefault();
          onDrawToolChange('typewriter');
        }
        return;
      }

      if (mode !== 'engineer' || !showEngineering) return;

      if (key === 'c') {
        e.preventDefault();
        onDrawToolChange('callout');
      } else if (key === 'l') {
        e.preventDefault();
        onDrawToolChange('line');
      } else if (key === 'f') {
        e.preventDefault();
        onDrawToolChange('stamp');
        setPaletteOpen(open => (drawTool === 'stamp' ? !open : true));
      } else if (key === 'e') {
        e.preventDefault();
        onDrawToolChange('eraser');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    mode,
    drawTool,
    hideMeasure,
    csvVariant,
    showEngineering,
    showShapeTools,
    onModeChange,
    onDrawToolChange,
  ]);

  return (
    <div
      className="pdf-viewer-mode-bar"
      role="toolbar"
      aria-label="Annotation tools"
      {...(tourAnchors ? { 'data-tour': 'portal-pdf-mode-bar' } : {})}
    >
      <div className="pdf-viewer-mode-bar-row">
      <div className="pdf-viewer-mode-toggle">
        <button
          type="button"
          className={`pdf-viewer-mode-btn ${mode === 'comment' ? 'active' : ''}`}
          onClick={() => onModeChange('comment')}
          title="Comments (1)"
          data-tip={tip('Comments', '1')}
          {...(tourAnchors ? { 'data-tour': 'portal-pdf-mode-comment' } : {})}
        >
          <MessageCircle size={16} />
          <span>Comments</span>
        </button>
        <button
          type="button"
          className={`pdf-viewer-mode-btn ${mode === 'draw' ? 'active' : ''}`}
          onClick={() => onModeChange('draw')}
          title="Draw (2)"
          data-tip={tip('Draw', '2')}
          {...(tourAnchors ? { 'data-tour': 'portal-pdf-mode-draw' } : {})}
        >
          <Pencil size={16} />
          <span>Draw</span>
        </button>
        {csvVariant ? (
          <button
            type="button"
            className={`pdf-viewer-mode-btn ${mode === 'highlight' ? 'active' : ''}`}
            onClick={() => onModeChange('highlight')}
            title="Highlight cells, rows, or columns"
            data-tip={tip('Highlight', 'H')}
          >
            <Highlighter size={16} />
            <span>Highlight</span>
          </button>
        ) : null}
        {!hideMeasure && !csvVariant ? (
          <button
            type="button"
            className={`pdf-viewer-mode-btn ${mode === 'measure' ? 'active' : ''}`}
            onClick={() => onModeChange('measure')}
            title="Measure (3)"
            data-tip={tip('Measure', '3')}
          >
            <Ruler size={16} />
            <span>Measure</span>
          </button>
        ) : null}
        {showEngineering && !csvVariant ? (
          <button
            type="button"
            className={`pdf-viewer-mode-btn pdf-viewer-mode-btn--engineer ${mode === 'engineer' ? 'active' : ''}`}
            onClick={() => onModeChange('engineer')}
            title="Engineering markup (4)"
            data-tip={tip('Engineer', '4')}
          >
            <PenTool size={16} />
            <span>Engineer</span>
          </button>
        ) : null}
      </div>
      {mode === 'draw' && (
        <div
          className="pdf-viewer-draw-tools"
          {...(tourAnchors ? { 'data-tour': 'portal-pdf-draw-tools' } : {})}
        >
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${drawTool === 'pen' ? 'active' : ''}`}
            onClick={() => onDrawToolChange('pen')}
            title="Pen (P)"
            data-tip={tip('Pen', 'P')}
            aria-label="Pen"
          >
            <Pencil size={16} />
          </button>
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${drawTool === 'highlighter' ? 'active' : ''}`}
            onClick={() => onDrawToolChange('highlighter')}
            title="Highlighter (H)"
            data-tip={tip('Highlighter', 'H')}
            aria-label="Highlighter"
          >
            <Highlighter size={16} />
          </button>
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${drawTool === 'eraser' ? 'active' : ''}`}
            onClick={() => onDrawToolChange('eraser')}
            title="Eraser (E)"
            data-tip={tip('Eraser', 'E')}
            aria-label="Eraser"
          >
            <Eraser size={16} />
          </button>
          {showShapeTools && !csvVariant ? (
            <>
              <button
                type="button"
                className={`pdf-viewer-tool-btn ${drawTool === 'cloud' ? 'active' : ''}`}
                onClick={() => onDrawToolChange('cloud')}
                title={tip('Revision cloud', 'C', 'Drag a rectangle to mark a revision area.')}
                data-tip={tip('Revision cloud', 'C', 'Drag a rectangle to mark a revision area.')}
                aria-label="Revision cloud"
              >
                <Cloud size={16} />
              </button>
              <button
                type="button"
                className={`pdf-viewer-tool-btn ${drawTool === 'typewriter' ? 'active' : ''}`}
                onClick={() => onDrawToolChange('typewriter')}
                title={tip('Typewriter', 'T', 'Click to place a typed note. Click again to edit.')}
                data-tip={tip('Typewriter', 'T', 'Click to place a typed note. Click again to edit.')}
                aria-label="Typewriter"
              >
                <Type size={16} />
              </button>
            </>
          ) : null}
          <div className="pdf-viewer-color-swatches" aria-label="Color">
            {SWATCHES.map(c => (
              <button
                key={c}
                type="button"
                className={`pdf-viewer-swatch ${drawColor === c ? 'selected' : ''}`}
                style={{ background: c }}
                onClick={() => onDrawColorChange(c)}
                title={c}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
          {showStrokeWidths && (
            <div className="pdf-viewer-stroke-widths" role="group" aria-label="Stroke size">
              {strokeWidths.map((w) => {
                const label = markupWidthLabel(w, drawTool);
                const chip = markupWidthChipPx(w, drawTool);
                return (
                  <button
                    key={w}
                    type="button"
                    className={`pdf-viewer-stroke-width${drawStrokeWidth === w ? ' selected' : ''}`}
                    title={`${label} stroke`}
                    aria-label={`${label} stroke`}
                    aria-pressed={drawStrokeWidth === w}
                    onClick={() => onDrawStrokeWidthChange?.(w)}
                  >
                    <span style={{ width: chip, height: chip }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      {mode === 'engineer' && showEngineering && (
        <div className="pdf-viewer-draw-tools pdf-viewer-engineer-tools">
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${drawTool === 'callout' ? 'active' : ''}`}
            onClick={() => onDrawToolChange('callout')}
            title={tip('Callout', 'C', `Drag from the detail to the note. Hold Shift for an elbow leader. Click a note to select it; click again to edit. ${DELETE_UNDO}`)}
            data-tip={tip('Callout', 'C', `Drag from the detail to the note. Hold Shift for an elbow leader. Click a note to select it; click again to edit. ${DELETE_UNDO}`)}
            data-tip-wrap=""
            aria-label="Callout"
          >
            <MessageSquare size={16} />
          </button>
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${drawTool === 'line' ? 'active' : ''}`}
            onClick={() => onDrawToolChange('line')}
            title={tip('Line', 'L', `Drag a straight line. Hold Shift and click to add polyline vertices; release Shift and click, or press Enter, to finish. ${DELETE_UNDO}`)}
            data-tip={tip('Line', 'L', `Drag a straight line. Hold Shift and click to add polyline vertices; release Shift and click, or press Enter, to finish. ${DELETE_UNDO}`)}
            data-tip-wrap=""
            aria-label="Line"
          >
            <Minus size={16} />
          </button>
          <div className="pdf-viewer-stamp-wrap">
            <button
              type="button"
              className={`pdf-viewer-tool-btn pdf-viewer-stamp-btn ${drawTool === 'stamp' ? 'active' : ''}`}
              onClick={() => {
                onDrawToolChange('stamp');
                setPaletteOpen(open => !open);
              }}
              title={tip('Symbols', 'F', `Click to place a symbol. Drag the box to move, corners to resize, top handle to rotate. ${DELETE_UNDO}`)}
              data-tip={tip('Symbols', 'F', `Click to place a symbol. Drag the box to move, corners to resize, top handle to rotate. ${DELETE_UNDO}`)}
              data-tip-wrap=""
              aria-label="Symbols"
              aria-expanded={paletteOpen}
            >
              <Combine size={16} />
            </button>
            {paletteOpen && drawTool === 'stamp' && (
              <div className="pdf-viewer-fastener-palette" role="listbox" aria-label="Symbol library">
                {(
                  [
                    { group: 'fastener' as const, title: 'Fasteners', sub: 'End view' },
                    { group: 'body' as const, title: 'Section', sub: 'Resize and rotate after placing' },
                  ] as const
                ).map(section => (
                  <React.Fragment key={section.group}>
                    <div className="pdf-viewer-fastener-palette-head">
                      <span>{section.title}</span>
                      <span className="pdf-viewer-fastener-palette-sub">{section.sub}</span>
                    </div>
                    <div className="pdf-viewer-fastener-grid">
                      {fastenersByGroup(section.group).map(f => (
                        <button
                          key={f.id}
                          type="button"
                          className={`pdf-viewer-fastener-cell ${selectedStampId === f.id ? 'active' : ''}`}
                          title={f.description}
                          onClick={() => {
                            onStampChange?.(f.id);
                            setPaletteOpen(false);
                          }}
                        >
                          <FastenerSymbol id={f.id} size={28} color="#1a1a1a" />
                          <span>{f.shortLabel}</span>
                        </button>
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${drawTool === 'eraser' ? 'active' : ''}`}
            onClick={() => onDrawToolChange('eraser')}
            title={tip('Eraser', 'E', `Click a mark to remove it. ${DELETE_UNDO}`)}
            data-tip={tip('Eraser', 'E', `Click a mark to remove it. ${DELETE_UNDO}`)}
            data-tip-wrap=""
            aria-label="Eraser"
          >
            <Eraser size={16} />
          </button>
          <div className="pdf-viewer-color-swatches" aria-label="Color">
            {ENGINEER_SWATCHES.map(c => (
              <button
                key={c}
                type="button"
                className={`pdf-viewer-swatch ${drawColor === c ? 'selected' : ''}`}
                style={{ background: c }}
                onClick={() => onDrawColorChange(c)}
                title={c}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
          {showStrokeWidths && (
            <div className="pdf-viewer-stroke-widths" role="group" aria-label="Stroke size">
              {strokeWidths.map((w) => {
                const label = markupWidthLabel(w, drawTool);
                const chip = markupWidthChipPx(w, drawTool);
                return (
                  <button
                    key={w}
                    type="button"
                    className={`pdf-viewer-stroke-width${drawStrokeWidth === w ? ' selected' : ''}`}
                    title={`${label} stroke`}
                    aria-label={`${label} stroke`}
                    aria-pressed={drawStrokeWidth === w}
                    onClick={() => onDrawStrokeWidthChange?.(w)}
                  >
                    <span style={{ width: chip, height: chip }} />
                  </button>
                );
              })}
            </div>
          )}
          {drawTool === 'stamp' ? <span className="pdf-viewer-stamp-name">{selectedStamp.shortLabel}</span> : null}
        </div>
      )}
      {mode === 'highlight' && csvVariant && (
        <div className="pdf-viewer-draw-tools">
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${highlightTool === 'cell' ? 'active' : ''}`}
            onClick={() => onHighlightToolChange?.('cell')}
            title="Highlight a single cell"
            aria-label="Cell highlight"
          >
            <Square size={16} />
          </button>
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${highlightTool === 'row' ? 'active' : ''}`}
            onClick={() => onHighlightToolChange?.('row')}
            title="Highlight a row"
            aria-label="Row highlight"
          >
            <Rows size={16} />
          </button>
          <button
            type="button"
            className={`pdf-viewer-tool-btn ${highlightTool === 'column' ? 'active' : ''}`}
            onClick={() => onHighlightToolChange?.('column')}
            title="Highlight a column"
            aria-label="Column highlight"
          >
            <Columns size={16} />
          </button>
          <div className="pdf-viewer-color-swatches" aria-label="Highlight color">
            {HIGHLIGHT_SWATCHES.map(c => (
              <button
                key={c}
                type="button"
                className={`pdf-viewer-swatch ${highlightColor === c ? 'selected' : ''}`}
                style={{ background: c }}
                onClick={() => onHighlightColorChange?.(c)}
                title={c}
                aria-label={`Highlight color ${c}`}
              />
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
